/**
 * Create form for a specialty.
 */
import * as React from 'react';
import { FileText, GraduationCap, ListOrdered } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { Checkbox } from '@/components/forms/Checkbox';
import {
  createSpecialty,
  getHighSchoolLevels,
  getSpecialties,
  reorderSpecialties,
} from '@/services/specialties.service';
import type { CreateSpecialtyDto, Specialty } from '@/types/specialty.types';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { SpecialtySortableList, NEW_SPECIALTY_PLACEHOLDER_ID } from './SpecialtySortableList';
import { SPECIALTY_FORM_QUERY_KEYS } from './specialty-configs';
import {
  applySpecialtyLabelChange,
  buildCreateSpecialtyDto,
  emptyCreateSpecialtyForm,
  formatSpecialtyLevelLabel,
  isSpecialtyFormValid,
  resolveCreatedSpecialtyOrder,
  addSpecialtyLevelId,
  removeSpecialtyLevelId,
  validateSpecialtyForm,
} from './specialty-form-utils';

/** Props for {@link CreateSpecialtyView}. */
interface CreateSpecialtyViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create form for a specialty. */
export const CreateSpecialtyView: React.FC<CreateSpecialtyViewProps> = ({ onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = React.useState<CreateSpecialtyDto>(emptyCreateSpecialtyForm);
  const [codeManuallyEdited, setCodeManuallyEdited] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [orderedIds, setOrderedIds] = React.useState<string[] | null>(null);

  const { data: highSchoolLevels = [] } = useQuery({
    queryKey: SPECIALTY_FORM_QUERY_KEYS.highSchoolLevels,
    queryFn: getHighSchoolLevels,
  });

  const { data: allSpecialties = [] } = useQuery({
    queryKey: SPECIALTY_FORM_QUERY_KEYS.all,
    queryFn: () => getSpecialties(false),
  });

  const createMutation = useMutation({
    mutationFn: async (dto: CreateSpecialtyDto) => {
      const created: Specialty = await createSpecialty(dto);
      const finalOrder = resolveCreatedSpecialtyOrder(
        orderedIds,
        NEW_SPECIALTY_PLACEHOLDER_ID,
        created.id,
      );
      if (finalOrder) {
        await reorderSpecialties(finalOrder);
        await queryClient.invalidateQueries({ queryKey: SPECIALTY_FORM_QUERY_KEYS.list });
      }
      return created;
    },
    onSuccess: () => {
      toast.success('Filière créée avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Changing the label auto-fills the code unless it was edited manually, then re-validates. */
  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = applySpecialtyLabelChange(formData, event.target.value, codeManuallyEdited);
    setFormData(next);
    setErrors(validateSpecialtyForm(next));
  };

  /** Manual code edit stops the label→code auto-slug, then re-validates. */
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCodeManuallyEdited(true);
    const next = { ...formData, code: event.target.value };
    setFormData(next);
    setErrors(validateSpecialtyForm(next));
  };

  /** Add or drop a linked level id. */
  const handleToggleLevel = (levelId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      levelIds: checked
        ? addSpecialtyLevelId(prev.levelIds ?? [], levelId)
        : removeSpecialtyLevelId(prev.levelIds ?? [], levelId),
    }));
  };

  /** Validate label / code then POST create DTO. */
  const handleSubmit = () => {
    const errs = validateSpecialtyForm(formData);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    createMutation.mutate(buildCreateSpecialtyDto(formData));
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une spécialité"
      subtitle="Renseignez les informations de la spécialité"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={isSpecialtyFormValid(formData)}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Informations générales
        </h4>

        <div className="grid grid-cols-1 gap-4">
          <Input
            label="Libellé"
            value={formData.label}
            onChange={handleLabelChange}
            placeholder="Ex: Mathématiques"
            maxLength={255}
            required
            helperText="Nom complet de la spécialité"
            error={errors.label}
            state={errors.label ? 'error' : 'default'}
          />

          <Input
            label="Code"
            value={formData.code}
            onChange={handleCodeChange}
            placeholder="Ex: mathematiques"
            maxLength={100}
            required
            helperText="Identifiant unique (généré depuis le libellé)"
            error={errors.code}
            state={errors.code ? 'error' : 'default'}
          />

          <Checkbox
            label="Spécialité active"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, isActive: checked }))}
            helperText="Une spécialité inactive n'apparaît pas dans les formulaires"
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <ListOrdered className="w-4 h-4" />
          Ordre dans la liste
        </h4>
        <p className="text-xs text-muted-foreground mb-3">
          Glissez-déposez pour définir l'ordre d'affichage
        </p>
        <SpecialtySortableList
          allSpecialties={allSpecialties}
          currentSpecialtyId={NEW_SPECIALTY_PLACEHOLDER_ID}
          newSpecialtyLabel={formData.label}
          onOrderChange={setOrderedIds}
        />
      </div>

      {highSchoolLevels.length > 0 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <GraduationCap className="w-4 h-4" />
            Niveaux lycée associés
          </h4>
          <div className="grid grid-cols-2 gap-3">
            {highSchoolLevels.map((level) => (
              <Checkbox
                key={level.id}
                id={`level-${level.id}`}
                label={formatSpecialtyLevelLabel(level)}
                checked={(formData.levelIds ?? []).includes(level.id)}
                onCheckedChange={(checked) => handleToggleLevel(level.id, checked)}
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Sélectionnez les niveaux lycée concernés par cette spécialité (optionnel)
          </p>
        </div>
      )}
    </SlidePanelForm>
  );
};
